import { z } from 'zod'
import { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from 'geojson'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import GeoJson from '~/components/googleMap/geoJson'
import GoogleMap from '~/components/googleMap/map'
import PolygonEditor, {
  DemarcationPoint,
  GhostDemarcationPoint,
  usePolygonEditor,
} from '~/components/googleMap/polygonEditor'
import { BaseActionDefinitionEnhanced, Type, fromUnknownError } from '~/prix'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import checkIntersects from '@turf/boolean-intersects'
import getDifference from '@turf/difference'
import getIntersect from '@turf/intersect'
import createBuffer from '@turf/buffer'
import { toastError, toastSuccess } from '~/components/toasts'
import Modal from '~/prix/react/components/modal'
import { omit } from '~/prix/utils/types/object'
import Marker from './googleMap/marker'
import { SmartMappingOptions } from '~/packages/geo/applySmartMapping/smartMapping.typing'
import {
  Layer,
  SpaceDelimitationGeoStandardWithShape,
} from '~/packages/general/variable/spaceTime.types'
import { faList } from '@fortawesome/free-solid-svg-icons'
import { featureCollection as createFeatureCollection } from '@turf/helpers'
import { transformScale } from '@turf/turf'
import theme from '~/design'
import createBbox from '@turf/bbox'
import { OverlayLoading } from '~/components/mapContent/styles/overlayLoading'
import viewListDefinition from '~/packages/general/view/list/viewList.definition'
import { useVariableFetchList } from './utils/hooks/useVariableFetchList'
import { useInitialSpaceLayer } from './utils/hooks/useInitialSpaceLayer'
import { Delimitation, useDelimitation } from './utils/hooks/useDelimitation'
import { ButtonVariants } from './buttonVariants'
import { ScreenWrapper } from './screenWrapper'
import { ListInformationWrapper } from './listInformationWrapper'
import { MainMenuControls } from './mainMenuControls'
import { LatLngBoundsLiteral, LatLngLiteral } from './map/mapPosition'

function positionsAreEqual(left: Position, right: Position): boolean {
  return left.length >= 2 && right.length >= 2 && left[0] === right[0] && left[1] === right[1]
}

function ensureClosedLinearRing(ring: Position[]): Position[] {
  if (ring.length === 0) {
    return ring
  }

  const first = ring[0]
  const last = ring[ring.length - 1]
  if (!positionsAreEqual(first, last)) {
    return [...ring, first]
  }

  return ring
}

function computeSignedAreaOfLinearRing(ring: Position[]): number {
  if (ring.length < 4) {
    return 0
  }

  const closedRing = ensureClosedLinearRing(ring)
  let signedArea = 0
  for (let index = 0; index < closedRing.length - 1; index++) {
    const current = closedRing[index]
    const next = closedRing[index + 1]
    signedArea += current[0] * next[1] - next[0] * current[1]
  }

  return signedArea / 2
}

function polygonOuterRingArea(polygon: Polygon): number {
  const outerRing = polygon.coordinates[0]
  if (!outerRing) {
    return 0
  }
  return Math.abs(computeSignedAreaOfLinearRing(outerRing))
}

function removePolygonHoles(polygon: Polygon): Polygon | null {
  const outerRing = polygon.coordinates[0]
  if (!outerRing) {
    return null
  }

  const closedOuterRing = ensureClosedLinearRing(outerRing)
  if (closedOuterRing.length < 4) {
    return null
  }

  return {
    type: 'Polygon',
    coordinates: [closedOuterRing],
  }
}

function getLargestPolygonFromMultiPolygon(multiPolygon: MultiPolygon): Polygon | null {
  let largestPolygon: Polygon | null = null
  let largestArea = 0

  for (const polygonCoordinates of multiPolygon.coordinates) {
    const candidatePolygon: Polygon = {
      type: 'Polygon',
      coordinates: polygonCoordinates,
    }

    const candidateArea = polygonOuterRingArea(candidatePolygon)
    if (candidateArea > largestArea) {
      largestArea = candidateArea
      largestPolygon = candidatePolygon
    }
  }

  return largestPolygon
}

function getLargestPolygonFromMultiPolygonPreservingHoles(multiPolygon: MultiPolygon): Polygon | null {
  let largestPolygonCoordinates: Position[][] | null = null
  let largestArea = 0

  for (const polygonCoordinates of multiPolygon.coordinates) {
    const candidatePolygon: Polygon = {
      type: 'Polygon',
      coordinates: polygonCoordinates,
    }

    const candidateArea = polygonOuterRingArea(candidatePolygon)
    if (candidateArea > largestArea) {
      largestArea = candidateArea
      largestPolygonCoordinates = polygonCoordinates
    }
  }

  if (!largestPolygonCoordinates) {
    return null
  }

  return {
    type: 'Polygon',
    coordinates: largestPolygonCoordinates,
  }
}

function simplifyCoverageAreaGeometry(geometry: Polygon | MultiPolygon): Polygon | null {
  if (geometry.type === 'Polygon') {
    return removePolygonHoles(geometry)
  }

  const largestPolygon = getLargestPolygonFromMultiPolygon(geometry)
  if (!largestPolygon) {
    return null
  }

  return removePolygonHoles(largestPolygon)
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) {
    return false
  }

  const tagName = target.tagName
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true
  }

  return target.isContentEditable
}

const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  height: 70vh;

  background-color: ${({ theme }) => theme.colors.lightGrey};

  .header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${({ theme }) => theme.padding(1)};
    padding: ${({ theme }) => theme.padding(2)};
    background-color: ${({ theme }) => theme.colors.lightText};
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .header-text {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .header-title {
      font-size: 14px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.primary};
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .header-subtitle {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.darkGrey};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .badges {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: ${({ theme }) => theme.padding(1)};
      flex: 0 0 auto;
    }

    .badge {
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 999px;
      background-color: ${({ theme }) => theme.colors.lightGrey};
      color: ${({ theme }) => theme.colors.darkText};
      border: 1px solid rgba(0, 0, 0, 0.08);
      user-select: none;
    }

    .badge-active {
      background-color: rgba(255, 105, 0, 0.12);
      border-color: rgba(255, 105, 0, 0.25);
      color: ${({ theme }) => theme.colors.secondary};
      font-weight: 700;
    }
  }

  .items {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: ${({ theme }) => theme.padding(1)};
  }

  ul {
    padding: 0;
    margin: 0;
    width: 100%;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.padding(1)};
  }

  li {
    text-decoration: none;
    line-height: 1.25;
    display: flex;
    flex-direction: column;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid rgba(0, 0, 0, 0.06);
    background-color: ${({ theme }) => theme.colors.lightText};
    overflow: hidden;
    transition: box-shadow ${({ theme }) => theme.transitionDurationMs}ms
      ${({ theme }) => theme.transitionTimingFunction};

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow(1)};
    }

    &.is-active {
      border-color: ${({ theme }) => theme.colors.secondary};
      box-shadow: ${({ theme }) => theme.shadow(1)};
    }
  }

  .item-card {
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.padding(1)};
    padding: ${({ theme }) => theme.padding(2)};
    width: 100%;

    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }

  .item-dot {
    width: 10px;
    height: 10px;
    margin-top: 3px;
    border-radius: 999px;
    flex: 0 0 auto;
    background-color: ${({ theme }) => theme.colors.mediumGrey};
  }

  .item-dot[data-mapped='true'] {
    background-color: ${({ theme }) => theme.colors.tertiary};
  }

  .item-text {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
  }

  .item-title {
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.darkText};
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-subtitle {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.darkGrey};
  }

  .item-content-wrapper {
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => theme.padding(2)};
    background-color: rgba(236, 240, 241, 0.65);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .footer {
    padding: ${({ theme }) => theme.padding(1)};
    background-color: ${({ theme }) => theme.colors.lightText};
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.padding(1)};
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.padding(1)};
    padding: ${({ theme }) => theme.padding(2)};
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.colors.lightGrey};
  }

  .section-title {
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
  }

  .actions-row {
    display: flex;
    gap: ${({ theme }) => theme.padding(1)};
  }

  .actions-row button {
    flex: 1;
    justify-content: center;
  }

  .section button {
    width: 100%;
    justify-content: flex-start;
  }

  .section.actions .actions-row button {
    justify-content: center;
  }
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: row;
  height: 100%;

  main {
    display: flex;
    flex: 1;
    flex-direction: column;

    .toolbar {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding: 1rem;
    }
  }
`
const WrapperSmartMapping = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;

  label {
    margin-bottom: 1rem;
  }
`

export const ItemContentWrapper = styled.div.attrs({
  className: 'item-content-wrapper',
})``

export interface BaseItem {
  key: string
  name: string
  coverageArea: MultiPolygon | Polygon | null
  kind?: string | null
  latitude?: number
  longitude?: number
}

export type Item = BaseItem

type ZSchema = z.ZodTypeAny

export interface EditCoverageAreaProps<
  Item extends BaseItem,
  FetchInSchema extends ZSchema = ZSchema,
  FetchOutSchema extends ZSchema = ZSchema,
  EditInSchema extends ZSchema = ZSchema,
  EditOutSchema extends ZSchema = ZSchema,
  SmartInSchema extends ZSchema = ZSchema,
  SmartOutSchema extends ZSchema = ZSchema,
> {
  recommendedSmartMappingOptions: SmartMappingOptions
  fetchDefinition: BaseActionDefinitionEnhanced<FetchInSchema, FetchOutSchema>
  editCoverageAreaDefinition: BaseActionDefinitionEnhanced<EditInSchema, EditOutSchema>
  smartMappingDefinition: BaseActionDefinitionEnhanced<SmartInSchema, SmartOutSchema>
  enablePointsEditing?: boolean
  visiblePolygons?: 'all' | 'neighbors' | 'intersected' | 'none'
  withTopology?: boolean
  compatibleDelimitationTypes?: string[]
  incompatibleDelimitationMessage?: string
  getSmartMappingBlockReason?: (delimitation: Delimitation | null) => string | null
  renderItemContent?: (item: Item, onChange: (item: Item) => void) => React.ReactNode
}

export const EditCoverageArea = <
  Item extends BaseItem,
  FetchInSchema extends ZSchema,
  FetchOutSchema extends ZSchema,
  EditInSchema extends ZSchema,
  EditOutSchema extends ZSchema,
  SmartInSchema extends ZSchema,
  SmartOutSchema extends ZSchema,
>({
  recommendedSmartMappingOptions,
  fetchDefinition,
  editCoverageAreaDefinition,
  smartMappingDefinition,
  enablePointsEditing = true,
  visiblePolygons = 'all',
  withTopology = true,
  compatibleDelimitationTypes = ['municipality'],
  incompatibleDelimitationMessage = 'Selecione um município',
  getSmartMappingBlockReason,
  renderItemContent,
}: EditCoverageAreaProps<
  Item,
  FetchInSchema,
  FetchOutSchema,
  EditInSchema,
  EditOutSchema,
  SmartInSchema,
  SmartOutSchema
>) => {
  type FeatureProperties = Omit<Item, 'coverageArea'>
  type MapPosition =
    | {
        bounds: LatLngBoundsLiteral
      }
    | {
        center: LatLngLiteral
        zoom: number
      }
  const [coveragesFeatureCollection, setCoveragesFeatureCollection] = useState<FeatureCollection<
    Polygon | null,
    FeatureProperties
  > | null>(null)
  // Estado: se o mapeamento inteligente está aberto
  const [isSmartMappingOpen, setIsSmartMappingOpen] = useState(false)
  const [smartMappingOptions, setSmartMappingOptions] = useState(recommendedSmartMappingOptions)

  // Estado: delimitação é compatível com a tela
  const [isCompatibleDelimitation, setIsCompatibleDelimitation] = useState<boolean>(true)

  // Estado: item selecionado
  const [selectedItem, setSelectedItem] = useState<Item | null>(null)

  // Estado: polígono selecionado
  const [selectedPolygonFeature, setSelectedPolygonFeature] = useState<Feature<
    Polygon,
    FeatureProperties
  > | null>(null)

  const [hasPendingPolygonEdits, setHasPendingPolygonEdits] = useState(false)
  const [hasUnsavedCoverageChanges, setHasUnsavedCoverageChanges] = useState(false)

  type EditorHistorySnapshot = {
    coveragesFeatureCollection: FeatureCollection<Polygon | null, FeatureProperties> | null
    selectedItem: Item | null
    selectedPolygonFeature: Feature<Polygon, FeatureProperties> | null
    polygonEditorPoints: Array<DemarcationPoint>
    hasPendingPolygonEdits: boolean
  }

  const maxHistorySize = 100
  const undoStackRef = useRef<EditorHistorySnapshot[]>([])
  const redoStackRef = useRef<EditorHistorySnapshot[]>([])
  const [, setHistoryVersion] = useState(0)
  const [restoreToken, setRestoreToken] = useState(0)
  const [mapNavigationPosition, setMapNavigationPosition] = useState<MapPosition | null>(null)

  const coveragesFeatureCollectionRef = useRef<FeatureCollection<
    Polygon | null,
    FeatureProperties
  > | null>(null)
  const selectedItemRef = useRef<Item | null>(null)
  const selectedPolygonFeatureRef = useRef<Feature<Polygon, FeatureProperties> | null>(null)
  const hasPendingPolygonEditsRef = useRef(false)
  const hasUnsavedCoverageChangesRef = useRef(false)
  const polygonEditorPointsRef = useRef<Array<DemarcationPoint>>([])
  const pendingRestoreSnapshotRef = useRef<EditorHistorySnapshot | null>(null)
  const delimitationSessionKeyRef = useRef<string | null>(null)

  coveragesFeatureCollectionRef.current = coveragesFeatureCollection
  selectedItemRef.current = selectedItem
  selectedPolygonFeatureRef.current = selectedPolygonFeature
  hasPendingPolygonEditsRef.current = hasPendingPolygonEdits
  hasUnsavedCoverageChangesRef.current = hasUnsavedCoverageChanges

  const markUnsavedCoverageChanges = useCallback(() => {
    if (hasUnsavedCoverageChangesRef.current) {
      return
    }
    hasUnsavedCoverageChangesRef.current = true
    setHasUnsavedCoverageChanges(true)
  }, [])

  const clearUnsavedCoverageChanges = useCallback(() => {
    if (!hasUnsavedCoverageChangesRef.current) {
      return
    }
    hasUnsavedCoverageChangesRef.current = false
    setHasUnsavedCoverageChanges(false)
  }, [])

  const getCurrentSnapshot = useCallback((): EditorHistorySnapshot => {
    return {
      coveragesFeatureCollection: coveragesFeatureCollectionRef.current,
      selectedItem: selectedItemRef.current,
      selectedPolygonFeature: selectedPolygonFeatureRef.current,
      polygonEditorPoints: polygonEditorPointsRef.current,
      hasPendingPolygonEdits: hasPendingPolygonEditsRef.current,
    }
  }, [])

  const pushUndoSnapshot = useCallback(() => {
    const snapshot = getCurrentSnapshot()
    const undoStack = undoStackRef.current
    const lastSnapshot = undoStack.length > 0 ? undoStack[undoStack.length - 1] : null
    if (
      lastSnapshot &&
      lastSnapshot.coveragesFeatureCollection === snapshot.coveragesFeatureCollection &&
      lastSnapshot.selectedItem === snapshot.selectedItem &&
      lastSnapshot.selectedPolygonFeature === snapshot.selectedPolygonFeature &&
      lastSnapshot.polygonEditorPoints === snapshot.polygonEditorPoints &&
      lastSnapshot.hasPendingPolygonEdits === snapshot.hasPendingPolygonEdits
    ) {
      return
    }

    undoStack.push(snapshot)
    if (undoStack.length > maxHistorySize) {
      undoStack.shift()
    }
    redoStackRef.current = []
    setHistoryVersion(version => version + 1)
  }, [getCurrentSnapshot])

  const restoreSnapshot = useCallback((snapshot: EditorHistorySnapshot) => {
    pendingRestoreSnapshotRef.current = snapshot
    setCoveragesFeatureCollection(snapshot.coveragesFeatureCollection)
    setSelectedItem(snapshot.selectedItem)
    setSelectedPolygonFeature(snapshot.selectedPolygonFeature)
    setHasPendingPolygonEdits(snapshot.hasPendingPolygonEdits)
    setRestoreToken(token => token + 1)
  }, [])

  const handleUndo = useCallback(() => {
    const undoStack = undoStackRef.current
    const previousSnapshot = undoStack.pop()
    if (!previousSnapshot) {
      return
    }

    const redoStack = redoStackRef.current
    redoStack.push(getCurrentSnapshot())
    if (redoStack.length > maxHistorySize) {
      redoStack.shift()
    }

    restoreSnapshot(previousSnapshot)
    setHistoryVersion(version => version + 1)
  }, [getCurrentSnapshot, restoreSnapshot])

  const handleRedo = useCallback(() => {
    const redoStack = redoStackRef.current
    const nextSnapshot = redoStack.pop()
    if (!nextSnapshot) {
      return
    }

    const undoStack = undoStackRef.current
    undoStack.push(getCurrentSnapshot())
    if (undoStack.length > maxHistorySize) {
      undoStack.shift()
    }

    restoreSnapshot(nextSnapshot)
    setHistoryVersion(version => version + 1)
  }, [getCurrentSnapshot, restoreSnapshot])

  // Seleção inicial de camadas base
  const [baseLayers, setBaseLayers] = useState<Layer[]>([])
  const isDelimitationChangedBetweenLayers = useCallback((previousLayers: Layer[], nextLayers: Layer[]) => {
    const previousSpace = previousLayers[0]?.space as
      | (Layer['space'] & { delimitationKey?: string | null })
      | undefined
    const nextSpace = nextLayers[0]?.space as
      | (Layer['space'] & { delimitationKey?: string | null })
      | undefined

    if (!previousSpace || !nextSpace) {
      return false
    }

    const previousDelimitationId =
      'delimitationId' in previousSpace ? previousSpace.delimitationId : null
    const nextDelimitationId = 'delimitationId' in nextSpace ? nextSpace.delimitationId : null

    return (
      previousSpace.delimitationType !== nextSpace.delimitationType ||
      previousDelimitationId !== nextDelimitationId ||
      (previousSpace.delimitationKey ?? null) !== (nextSpace.delimitationKey ?? null)
    )
  }, [])

  const setBaseLayersWithUnsavedConfirmation = useCallback(
    (value: Layer[] | ((layers: Layer[]) => Layer[])) => {
      let wasUpdated = false
      let changedDelimitation = false

      setBaseLayers(previousLayers => {
        const nextLayers = typeof value === 'function' ? value(previousLayers) : value
        changedDelimitation = isDelimitationChangedBetweenLayers(previousLayers, nextLayers)

        const hasUnsavedChanges =
          hasPendingPolygonEditsRef.current || hasUnsavedCoverageChangesRef.current
        if (changedDelimitation && hasUnsavedChanges) {
          const hasConfirmed = window.confirm(
            'Existem alterações não salvas. Deseja trocar a delimitação e descartar essas alterações?',
          )
          if (!hasConfirmed) {
            return previousLayers
          }
        }

        wasUpdated = true
        return nextLayers
      })

      if (wasUpdated && changedDelimitation) {
        setHasPendingPolygonEdits(false)
        clearUnsavedCoverageChanges()
      }
    },
    [clearUnsavedCoverageChanges, isDelimitationChangedBetweenLayers],
  )
  const layers: Layer[] = useMemo(() => {
    return baseLayers
  }, [baseLayers])

  // Hook para obter a lista de variáveis
  const { variables, currentTool, variablesAreLoading } = useVariableFetchList(viewListDefinition)

  // Hook para obter shape inicial da localidade
  const { initialSpaceLayer, isLoadingInitialSpace } = useInitialSpaceLayer()

  // Hook para navegar por posição de mapa
  const { delimitation, setDelimitation, currentResult, mapPosition } = useDelimitation(
    fetchDefinition,
    setCoveragesFeatureCollection,
    setIsCompatibleDelimitation,
    compatibleDelimitationTypes,
  )

  useEffect(() => {
    if (!currentResult.result) {
      return
    }
    clearUnsavedCoverageChanges()
    setHasPendingPolygonEdits(false)
  }, [clearUnsavedCoverageChanges, currentResult.result])

  useEffect(() => {
    const sessionKey = delimitation
      ? `${delimitation.type}:${delimitation.id}:${delimitation.key ?? ''}`
      : null
    if (sessionKey === delimitationSessionKeyRef.current) {
      return
    }

    delimitationSessionKeyRef.current = sessionKey
    undoStackRef.current = []
    redoStackRef.current = []
    setHistoryVersion(version => version + 1)
    setHasPendingPolygonEdits(false)
    clearUnsavedCoverageChanges()
    setSelectedItem(null)
    setSelectedPolygonFeature(null)
    setMapNavigationPosition(mapPosition ?? null)
  }, [clearUnsavedCoverageChanges, delimitation?.id, delimitation?.key, delimitation?.type, mapPosition])

  const mapNavigationForItem = useCallback(
    (
      item: FeatureProperties & {
        coverageArea: Polygon | MultiPolygon | null
      },
    ): MapPosition | null => {
      if (item.coverageArea) {
        try {
          const polygonResized = transformScale(item.coverageArea, 1.3)
          const bbox = createBbox(polygonResized)
          const bounds: LatLngBoundsLiteral = {
            west: bbox[0],
            south: bbox[1],
            east: bbox[2],
            north: bbox[3],
          }
          return { bounds }
        } catch {
          // Fallback to point when geometry is malformed.
        }
      }

      if (typeof item.latitude === 'number' && typeof item.longitude === 'number') {
        return {
          center: { lat: item.latitude, lng: item.longitude },
          zoom: 16,
        }
      }

      return null
    },
    [],
  )

  const delimitationFeatureCollection = useMemo(() => {
    if (!delimitation) {
      return null
    }
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            key: 'delimitation',
            name: delimitation.name,
          },
          geometry: delimitation.shape,
        },
      ],
    } as FeatureCollection<Polygon | MultiPolygon, { key: string; name: string }>
  }, [delimitation])

  useEffect(() => {
    const space = layers[0]?.space
    if (!space) return
    // Se não houver shape, define a delimitação inicial
    if (!(space as SpaceDelimitationGeoStandardWithShape).shape) {
      setDelimitation(initialSpaceLayer)
    } else {
      setDelimitation({
        id: (space as SpaceDelimitationGeoStandardWithShape).delimitationId,
        key: (space as { delimitationKey?: string | null }).delimitationKey ?? null,
        name: (space as SpaceDelimitationGeoStandardWithShape).delimitationTitle ?? '',
        type: (space as SpaceDelimitationGeoStandardWithShape).delimitationType as string,
        shape: (space as SpaceDelimitationGeoStandardWithShape).shape as Polygon | MultiPolygon,
      } as Delimitation)
    }
  }, [initialSpaceLayer, layers[0]?.space])

  // Memoização das coberturas / polígonos filtrados
  const filteredCoveragesFeatureCollection = useMemo(() => {
    if (!coveragesFeatureCollection) {
      return null
    }

    let features = coveragesFeatureCollection.features.filter(
      feature => feature.properties.key !== selectedItem?.key,
    )

    let currentPolygonFeatureSelected = coveragesFeatureCollection.features.find(
      feature => feature.properties.key === selectedItem?.key,
    ) as Feature<Polygon> | null

    if (visiblePolygons === 'neighbors' || visiblePolygons === 'intersected') {
      if (visiblePolygons === 'neighbors') {
        currentPolygonFeatureSelected =
          currentPolygonFeatureSelected && currentPolygonFeatureSelected.geometry
            ? (createBuffer(currentPolygonFeatureSelected, 0.01) as Feature<Polygon> | null)
            : null
      }

      features = features.filter(
        feature =>
          currentPolygonFeatureSelected &&
          currentPolygonFeatureSelected.geometry &&
          feature.geometry &&
          checkIntersects(
            currentPolygonFeatureSelected as Feature<Polygon>,
            feature as Feature<Polygon>,
          ) &&
          getIntersect(
            createFeatureCollection([feature as Feature<Polygon>, currentPolygonFeatureSelected]),
          ),
      )
    } else if (visiblePolygons === 'none') {
      features = []
    }

    return {
      type: 'FeatureCollection',
      features,
    } as FeatureCollection<Polygon | null, FeatureProperties>
  }, [selectedItem, coveragesFeatureCollection, visiblePolygons])

  // Aplicação do polígono selecionado
  const polygonEditor = usePolygonEditor(selectedPolygonFeature)
  polygonEditorPointsRef.current = polygonEditor.points

  useEffect(() => {
    setSelectedPolygonFeature(polygonEditor.polygon as Feature<Polygon, FeatureProperties> | null)
  }, [polygonEditor.polygon])

  useEffect(() => {
    const snapshotToRestore = pendingRestoreSnapshotRef.current
    if (!snapshotToRestore) {
      return
    }

    pendingRestoreSnapshotRef.current = null
    polygonEditor.setActiveGhostPoint(null)
    polygonEditor.setPoints(snapshotToRestore.polygonEditorPoints)
    polygonEditor.setIsDirty(snapshotToRestore.hasPendingPolygonEdits)
  }, [
    restoreToken,
    polygonEditor.setActiveGhostPoint,
    polygonEditor.setIsDirty,
    polygonEditor.setPoints,
  ])

  const applyPolygon = useCallback(() => {
    if (!selectedItem || !filteredCoveragesFeatureCollection || !polygonEditor.polygon) {
      return coveragesFeatureCollection
    }

    const newCoverageArea: Feature<Polygon, FeatureProperties> = {
      type: 'Feature',
      properties: {
        ...omit(selectedItem, ['coverageArea']),
        kind: 'manualDrawing',
      },
      geometry: polygonEditor.polygon.geometry,
    }

    if (!withTopology) {
      const newCoveragesFeatureCollection: FeatureCollection<Polygon | null, FeatureProperties> = {
        type: 'FeatureCollection',
        features: [
          ...filteredCoveragesFeatureCollection.features.filter(
            feature => feature.properties.key !== selectedItem.key,
          ),
          newCoverageArea,
        ],
      }
      setCoveragesFeatureCollection(newCoveragesFeatureCollection)
      markUnsavedCoverageChanges()

      return newCoveragesFeatureCollection
    }

    const intersectedFeatures = filteredCoveragesFeatureCollection.features.filter(
      feature =>
        feature.geometry &&
        checkIntersects(newCoverageArea, feature) &&
        getIntersect(createFeatureCollection([feature as Feature<Polygon>, newCoverageArea])),
    ) as Array<Feature<Polygon>>
    const notIntersectedFeatures = filteredCoveragesFeatureCollection.features.filter(
      feature => intersectedFeatures.indexOf(feature as Feature<Polygon>) === -1,
    )
    const intersectedFeaturesUpdated = intersectedFeatures.map(feature => {
      const subtracted = getDifference(createFeatureCollection([feature, newCoverageArea]))
      return {
        type: 'Feature',
        properties: {
          ...feature.properties,
          kind: 'manualDrawing',
        },
        geometry: subtracted?.geometry ?? null,
      } as Feature<Polygon, FeatureProperties>
    })

    // Order by name
    const newFeatures = [
      ...notIntersectedFeatures,
      newCoverageArea,
      ...intersectedFeaturesUpdated,
    ].sort((a, b) => (a.properties?.name > b.properties?.name ? 1 : -1))
    const newCoveragesFeatureCollection = {
      type: 'FeatureCollection',
      features: newFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>

    setCoveragesFeatureCollection(newCoveragesFeatureCollection)
    markUnsavedCoverageChanges()
    return newCoveragesFeatureCollection
  }, [
    selectedItem,
    filteredCoveragesFeatureCollection,
    polygonEditor,
    withTopology,
    markUnsavedCoverageChanges,
  ])

  const appendPolygonEditorPoint = useCallback(
    (position: google.maps.LatLngLiteral) => {
      if (!selectedItemRef.current) {
        return
      }

      pushUndoSnapshot()
      setHasPendingPolygonEdits(true)
      polygonEditor.appendPoint(position)
    },
    [polygonEditor.appendPoint, pushUndoSnapshot],
  )

  const handlePolygonEditorMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      const { latLng } = event
      if (!latLng) {
        return
      }

      appendPolygonEditorPoint(latLng.toJSON())
    },
    [appendPolygonEditorPoint],
  )

  const handlePolygonEditorMarkerDragEnd = useCallback(
    (point: DemarcationPoint, index: number) => {
      const handler = polygonEditor.onMarkerDragEnd(point, index)
      return (event: google.maps.MapMouseEvent) => {
        if (!selectedItemRef.current) {
          return
        }

        pushUndoSnapshot()
        setHasPendingPolygonEdits(true)
        handler(event)
      }
    },
    [polygonEditor.onMarkerDragEnd, pushUndoSnapshot],
  )

  const handlePolygonEditorGhostMarkerDragEnd = useCallback(
    (point: GhostDemarcationPoint) => {
      const handler = polygonEditor.onGhostMarkerDragEnd(point)
      return (event: google.maps.MapMouseEvent) => {
        if (!selectedItemRef.current) {
          return
        }

        pushUndoSnapshot()
        setHasPendingPolygonEdits(true)
        handler(event)
      }
    },
    [polygonEditor.onGhostMarkerDragEnd, pushUndoSnapshot],
  )

  const handlePolygonEditorMarkerClick = useCallback(
    (point: DemarcationPoint, index: number) => {
      const handler = polygonEditor.onMarkerClick(point, index)
      return (event: google.maps.MapMouseEvent) => {
        if (!selectedItemRef.current) {
          return
        }

        pushUndoSnapshot()
        setHasPendingPolygonEdits(true)
        handler(event)
      }
    },
    [polygonEditor.onMarkerClick, pushUndoSnapshot],
  )

  // Seleção de polígono ao selecionar item na lista
  useEffect(() => {
    if (!selectedItem) {
      setSelectedPolygonFeature(null)
      return
    }

    if (hasPendingPolygonEdits) {
      return
    }

    setSelectedPolygonFeature(
      (coveragesFeatureCollection?.features.find(
        feature => feature.geometry && feature.properties!.key === selectedItem?.key,
      ) ?? null) as Feature<Polygon, FeatureProperties> | null,
    )
  }, [selectedItem, coveragesFeatureCollection, hasPendingPolygonEdits])

  // Memoização dos pontos dos marcadores
  const handleClickMarker = useCallback(
    (point: Item | null) => {
      if (hasPendingPolygonEditsRef.current) {
        pushUndoSnapshot()
        applyPolygon()
        setHasPendingPolygonEdits(false)
      }

      if (point) {
        setSelectedItem(point as unknown as Item)
      } else {
        setSelectedItem(null)
      }
    },
    [applyPolygon, pushUndoSnapshot],
  )

  const handleDragEndSelectedMarker = useCallback(
    (_event: google.maps.MapMouseEvent, marker: google.maps.Marker) => {
      if (!selectedItem) {
        return
      }

      const position = marker.getPosition()
      if (!position) {
        return
      }

      pushUndoSnapshot()

      const latitude = position.lat()
      const longitude = position.lng()
      const selectedKey = selectedItem.key

      setSelectedItem(item => (item ? ({ ...item, latitude, longitude } as Item) : item))
      setCoveragesFeatureCollection(featureCollection => {
        if (!featureCollection) {
          return featureCollection
        }

        const updatedFeatures = featureCollection.features.map(feature => {
          if (feature.properties.key !== selectedKey) {
            return feature
          }

          return {
            ...feature,
            properties: {
              ...feature.properties,
              latitude,
              longitude,
            },
          }
        })

        return {
          ...featureCollection,
          features: updatedFeatures as Feature<Polygon | null, FeatureProperties>[],
        }
      })
      markUnsavedCoverageChanges()
    },
    [pushUndoSnapshot, selectedItem, markUnsavedCoverageChanges],
  )

  const points = useMemo(() => {
    if (!coveragesFeatureCollection || !enablePointsEditing) {
      return []
    }
    const coveragesFeatureCollectionPoints = coveragesFeatureCollection.features
      .filter(
        feature =>
          typeof feature.properties.latitude === 'number' &&
          typeof feature.properties.longitude === 'number',
      )
      .map(item => ({
        latitude: item?.properties?.latitude!,
        longitude: item?.properties?.longitude!,
        key: item?.properties?.key,
        name: item?.properties?.name,
        kind: item?.properties?.kind,
        coverageArea: item?.geometry as Polygon | MultiPolygon | null,
      }))
    if (!selectedItem) {
      return coveragesFeatureCollectionPoints.map((point, index) => (
        <Marker
          key={`marker-${point.key}`}
          position={{ lat: point.latitude, lng: point.longitude }}
          onClick={() => handleClickMarker(point as unknown as Item)}
        />
      ))
    }
    if (selectedItem && selectedItem.latitude && selectedItem.longitude) {
      return (
        <Marker
          position={{ lat: selectedItem.latitude, lng: selectedItem.longitude }}
          onClick={() => handleClickMarker(null)}
          onDragEnd={handleDragEndSelectedMarker}
        />
      )
    }
  }, [
    coveragesFeatureCollection,
    enablePointsEditing,
    handleClickMarker,
    handleDragEndSelectedMarker,
    selectedItem,
  ])

  const handlePolygonClick = useCallback(
    (event: google.maps.Data.MouseEvent) => {
      const activeItem = selectedItemRef.current
      const shouldDrawOnPolygonClick =
        Boolean(activeItem) &&
        (hasPendingPolygonEditsRef.current ||
          (activeItem?.coverageArea == null && polygonEditorPointsRef.current.length === 0))

      // While editing (or when starting a new polygon from scratch), clicks on other polygons
      // should behave as drawing interactions instead of selecting a different item.
      if (shouldDrawOnPolygonClick) {
        const { latLng } = event
        if (!latLng) {
          return
        }
        appendPolygonEditorPoint(latLng.toJSON())
        return
      }

      if (hasPendingPolygonEditsRef.current) {
        pushUndoSnapshot()
        applyPolygon()
        setHasPendingPolygonEdits(false)
      }

      const clickedKey = event.feature.getProperty('key') as string
      const feature = (filteredCoveragesFeatureCollection?.features.find(
        feature => feature.properties.key === clickedKey,
      ) ?? null) as Feature<Polygon, FeatureProperties> | null

      if (!feature) {
        return
      }

      setSelectedItem({
        ...feature.properties,
        coverageArea: feature.geometry,
      } as Item)

      requestAnimationFrame(() => {
        const element = document.getElementById(`li-${clickedKey}`)
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    [appendPolygonEditorPoint, applyPolygon, filteredCoveragesFeatureCollection, pushUndoSnapshot],
  )

  // Troca de item na lista
  const handleChangeItem = useCallback(
    (item: Item) => {
      setSelectedItem(item)

      if (!coveragesFeatureCollection) {
        return
      }

      const newFeatures = coveragesFeatureCollection?.features.map(feature => {
        if (feature.properties.key === item.key) {
          return {
            ...feature,
            properties: omit(item, ['coverageArea']),
            geometry: item.coverageArea,
          }
        }
        return feature
      })
      setCoveragesFeatureCollection({
        type: 'FeatureCollection',
        features: newFeatures as Feature<Polygon, FeatureProperties>[],
      })
      markUnsavedCoverageChanges()
    },
    [coveragesFeatureCollection, markUnsavedCoverageChanges],
  )

  // Action para salvar e aplicar o polígono
  const save = useActionFunction(editCoverageAreaDefinition, {
    initialIsLoading: false,
  })

  const handleSave = useCallback(async () => {
    if (!delimitation) {
      return
    }

    try {
      if (hasPendingPolygonEditsRef.current) {
        pushUndoSnapshot()
      }
      const newFeatureCollection = applyPolygon()
      setHasPendingPolygonEdits(false)

      await save.callAction({
        featureCollection: newFeatureCollection,
        delimitationPolygon: delimitation.shape,
        delimitationId: delimitation.id,
        delimitationKey: delimitation.key,
        delimitationType: delimitation.type,
      }).resultPromise
      setSelectedItem(null)
      clearUnsavedCoverageChanges()
      toastSuccess('Atualizado com sucesso')
    } catch (error) {
      console.error(error)
      const finalError = fromUnknownError(error as Error)
      toastError(finalError.message)
    }
  }, [applyPolygon, clearUnsavedCoverageChanges, delimitation, pushUndoSnapshot])

  // Action para mapeamento inteligente
  const smartMapping = useActionFunction(smartMappingDefinition, {
    initialIsLoading: false,
  })

  const handleRemoveFragments = useCallback(() => {
    if (delimitation?.shape.type === 'MultiPolygon' && delimitation.shape.coordinates.length > 1) {
      return
    }

    pushUndoSnapshot()

    const baseFeatureCollection = hasPendingPolygonEditsRef.current
      ? (applyPolygon() ?? coveragesFeatureCollectionRef.current)
      : coveragesFeatureCollectionRef.current
    setHasPendingPolygonEdits(false)
    if (!baseFeatureCollection) {
      return
    }

    const updatedFeatures = baseFeatureCollection.features.map(feature => {
      const geometry = feature.geometry
      if (!geometry) {
        return feature
      }

      if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
        const simplifiedGeometry = simplifyCoverageAreaGeometry(geometry)
        return {
          ...feature,
          geometry: simplifiedGeometry,
        }
      }

      return feature
    })

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem(currentSelectedItem => {
      if (!currentSelectedItem) {
        return currentSelectedItem
      }
      const updatedFeature = updatedFeatures.find(
        feature => feature.properties.key === currentSelectedItem.key,
      )
      if (!updatedFeature) {
        return currentSelectedItem
      }
      return {
        ...currentSelectedItem,
        coverageArea: updatedFeature.geometry ?? null,
      } as Item
    })

    toastSuccess('Fragmentos removidos com sucesso')
  }, [applyPolygon, delimitation?.shape, pushUndoSnapshot, markUnsavedCoverageChanges])

  const handleResolvePolygons = useCallback(() => {
    if (!selectedItem || !polygonEditor.polygon) {
      return
    }

    if (!polygonEditor.polygonIsValid.status) {
      toastError(polygonEditor.polygonIsValid.errorMessage || 'Polígono inválido')
      return
    }

    pushUndoSnapshot()
    applyPolygon()
    setHasPendingPolygonEdits(false)
    setSelectedItem(null)
    setSelectedPolygonFeature(null)
    markUnsavedCoverageChanges()
    toastSuccess('Polígonos resolvidos com sucesso')
  }, [
    applyPolygon,
    markUnsavedCoverageChanges,
    polygonEditor.polygon,
    polygonEditor.polygonIsValid,
    pushUndoSnapshot,
    selectedItem,
  ])

  const handleClipByDelimitation = useCallback(() => {
    if (!delimitation?.shape) {
      return
    }

    pushUndoSnapshot()

    const baseFeatureCollection = hasPendingPolygonEditsRef.current
      ? (applyPolygon() ?? coveragesFeatureCollectionRef.current)
      : coveragesFeatureCollectionRef.current
    setHasPendingPolygonEdits(false)
    if (!baseFeatureCollection) {
      return
    }

    const delimitationFeature: Feature<Polygon | MultiPolygon, Record<string, never>> = {
      type: 'Feature',
      properties: {},
      geometry: delimitation.shape,
    }

    const updatedFeatures = baseFeatureCollection.features.map(feature => {
      const geometry = feature.geometry
      if (!geometry) {
        return feature
      }

      try {
        const intersection = getIntersect(
          createFeatureCollection([
            feature as unknown as Feature<Polygon | MultiPolygon>,
            delimitationFeature,
          ]),
        ) as unknown as Feature<Polygon | MultiPolygon> | null

        return {
          ...feature,
          geometry: intersection?.geometry ?? null,
        }
      } catch (error) {
        console.error(error)
        return {
          ...feature,
          geometry: null,
        }
      }
    })

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem(currentSelectedItem => {
      if (!currentSelectedItem) {
        return currentSelectedItem
      }
      const updatedFeature = updatedFeatures.find(
        feature => feature.properties.key === currentSelectedItem.key,
      )
      if (!updatedFeature) {
        return currentSelectedItem
      }
      return {
        ...currentSelectedItem,
        coverageArea: updatedFeature.geometry ?? null,
      } as Item
    })

    toastSuccess('Corte pela delimitação aplicado com sucesso')
  }, [applyPolygon, delimitation?.shape, pushUndoSnapshot, markUnsavedCoverageChanges])

  const handleFillEntireDelimitation = useCallback(() => {
    if (!delimitation?.shape) {
      return
    }

    const currentSelectedItem = selectedItemRef.current
    if (!currentSelectedItem) {
      return
    }

    const baseFeatureCollection = coveragesFeatureCollectionRef.current
    if (!baseFeatureCollection) {
      return
    }

    const delimitationFeature: Feature<Polygon | MultiPolygon, Record<string, never>> = {
      type: 'Feature',
      properties: {},
      geometry: delimitation.shape,
    }

    let remainingAreaFeature: Feature<Polygon | MultiPolygon, Record<string, never>> | null =
      delimitationFeature

    for (const feature of baseFeatureCollection.features) {
      if (!remainingAreaFeature) {
        break
      }

      if (feature.properties.key === currentSelectedItem.key) {
        continue
      }

      const geometry = feature.geometry
      if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) {
        continue
      }

      const otherFeature: Feature<Polygon | MultiPolygon, Record<string, never>> = {
        type: 'Feature',
        properties: {},
        geometry,
      }

      const hasIntersection =
        checkIntersects(remainingAreaFeature, otherFeature) &&
        Boolean(getIntersect(createFeatureCollection([remainingAreaFeature, otherFeature])))

      if (!hasIntersection) {
        continue
      }

      try {
        const nextRemainingArea = getDifference(
          createFeatureCollection([remainingAreaFeature, otherFeature]),
        ) as unknown as Feature<Polygon | MultiPolygon> | null

        remainingAreaFeature = nextRemainingArea
          ? ({
              type: 'Feature',
              properties: {},
              geometry: nextRemainingArea.geometry,
            } as Feature<Polygon | MultiPolygon, Record<string, never>>)
          : null
      } catch (error) {
        console.error(error)
        toastError('Não foi possível preencher a área. Tente resolver os polígonos antes.')
        return
      }
    }

    const remainingGeometry = remainingAreaFeature?.geometry ?? null

    let finalPolygon: Polygon | null = null
    if (remainingGeometry?.type === 'Polygon') {
      finalPolygon = remainingGeometry
    } else if (remainingGeometry?.type === 'MultiPolygon') {
      finalPolygon = getLargestPolygonFromMultiPolygonPreservingHoles(remainingGeometry)
    }

    pushUndoSnapshot()
    setHasPendingPolygonEdits(false)
    polygonEditor.setActiveGhostPoint(null)
    polygonEditor.setIsDirty(false)

    const updatedFeatures = baseFeatureCollection.features.map(feature => {
      if (feature.properties.key !== currentSelectedItem.key) {
        return feature
      }

      return {
        ...feature,
        properties: {
          ...feature.properties,
          kind: 'manualDrawing',
        },
        geometry: finalPolygon,
      }
    })

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem({
      ...currentSelectedItem,
      kind: 'manualDrawing',
      coverageArea: finalPolygon,
    } as Item)

    toastSuccess('Área preenchida com sucesso')
  }, [
    delimitation?.shape,
    markUnsavedCoverageChanges,
    polygonEditor.setActiveGhostPoint,
    polygonEditor.setIsDirty,
    pushUndoSnapshot,
  ])

  const handleDeleteSelectedPolygon = useCallback(() => {
    const currentSelectedItem = selectedItemRef.current
    const baseFeatureCollection = coveragesFeatureCollectionRef.current
    if (!currentSelectedItem || !baseFeatureCollection) {
      return
    }

    pushUndoSnapshot()
    setHasPendingPolygonEdits(false)
    polygonEditor.setActiveGhostPoint(null)
    polygonEditor.setPoints([])
    polygonEditor.setIsDirty(false)
    setSelectedPolygonFeature(null)

    const updatedFeatures = baseFeatureCollection.features.map(feature => {
      if (feature.properties.key !== currentSelectedItem.key) {
        return feature
      }

      return {
        ...feature,
        properties: {
          ...feature.properties,
          kind: null,
        },
        geometry: null,
      }
    })

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem({
      ...currentSelectedItem,
      kind: null,
      coverageArea: null,
    } as Item)

    toastSuccess('Polígono selecionado apagado com sucesso')
  }, [
    markUnsavedCoverageChanges,
    polygonEditor.setActiveGhostPoint,
    polygonEditor.setIsDirty,
    polygonEditor.setPoints,
    pushUndoSnapshot,
  ])

  const handleClearAllPolygons = useCallback(() => {
    const baseFeatureCollection = coveragesFeatureCollectionRef.current
    if (!baseFeatureCollection) {
      return
    }

    const hasConfirmed = window.confirm(
      'Isso irá remover os polígonos de todos os itens. Deseja continuar?',
    )
    if (!hasConfirmed) {
      return
    }

    pushUndoSnapshot()
    setHasPendingPolygonEdits(false)
    polygonEditor.setActiveGhostPoint(null)
    polygonEditor.setPoints([])
    polygonEditor.setIsDirty(false)
    setSelectedPolygonFeature(null)

    const updatedFeatures = baseFeatureCollection.features.map(feature => ({
      ...feature,
      properties: {
        ...feature.properties,
        kind: null,
      },
      geometry: null,
    }))

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem(currentSelectedItem => {
      if (!currentSelectedItem) {
        return currentSelectedItem
      }

      return {
        ...currentSelectedItem,
        kind: null,
        coverageArea: null,
      } as Item
    })

    toastSuccess('Todos os polígonos foram removidos com sucesso')
  }, [
    markUnsavedCoverageChanges,
    polygonEditor.setActiveGhostPoint,
    polygonEditor.setIsDirty,
    polygonEditor.setPoints,
    pushUndoSnapshot,
  ])

  // Atualização de polígonos usando mapeamento inteligente
  const handleUpdatePolygonsUsingSmartMapping = useCallback(async () => {
    if (!delimitation?.shape) {
      return
    }

    const smartMappingBlockReason = getSmartMappingBlockReason?.(delimitation) ?? null
    if (smartMappingBlockReason) {
      toastError(smartMappingBlockReason)
      return
    }

    try {
      const response = await smartMapping.callAction({
        ...smartMappingOptions,
        delimitationPolygon: delimitation.shape,
        delimitationId: delimitation.id,
        delimitationKey: delimitation.key,
        delimitationType: delimitation.type,
      }).resultPromise
      const newFeatureCollectionUpdated = response.featureCollection
      pushUndoSnapshot()
      setCoveragesFeatureCollection(newFeatureCollectionUpdated)
      setHasPendingPolygonEdits(false)
      markUnsavedCoverageChanges()
      toastSuccess('Mapeamento inteligente realizado com sucesso, pronto para editar e salvar')
      setIsSmartMappingOpen(false)
    } catch (error) {
      console.error(error)
      const finalError = fromUnknownError(error as Error)
      toastError(finalError.message)
    }
  }, [
    delimitation,
    getSmartMappingBlockReason,
    markUnsavedCoverageChanges,
    pushUndoSnapshot,
    smartMappingOptions,
  ])

  const currentMapPosition = useMemo(() => {
    return mapNavigationPosition ?? mapPosition
  }, [mapNavigationPosition, mapPosition])

  const isLoading = variablesAreLoading || isLoadingInitialSpace
  const isLoadingFeature = currentResult.isLoading || save.isLoading || smartMapping.isLoading
  const smartMappingBlockReason = getSmartMappingBlockReason?.(delimitation) ?? null
  const canUndo = undoStackRef.current.length > 0
  const canRedo = redoStackRef.current.length > 0
  const delimitationHasMultiplePolygons = Boolean(
    delimitation?.shape &&
      delimitation.shape.type === 'MultiPolygon' &&
      delimitation.shape.coordinates.length > 1,
  )
  const hasSelectedPolygon = Boolean(
    selectedItem &&
      (selectedItem.coverageArea ||
        selectedPolygonFeature?.geometry ||
        (hasPendingPolygonEdits && polygonEditor.points.length > 0)),
  )
  const hasAnyPolygon = Boolean(
    coveragesFeatureCollection?.features.some(feature => Boolean(feature.geometry)) ||
      (hasPendingPolygonEdits && polygonEditor.points.length > 0),
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isLoadingFeature) {
        return
      }

      const withModifier = event.ctrlKey || event.metaKey
      if (!withModifier) {
        return
      }

      if (isEditableTarget(event.target)) {
        return
      }

      const key = event.key.toLowerCase()
      const isUndoKey = key === 'z' && !event.shiftKey
      const isRedoKey = key === 'y' || (key === 'z' && event.shiftKey)
      if (!isUndoKey && !isRedoKey) {
        return
      }

      event.preventDefault()
      if (isUndoKey && undoStackRef.current.length > 0) {
        handleUndo()
      }
      if (isRedoKey && redoStackRef.current.length > 0) {
        handleRedo()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [handleRedo, handleUndo, isLoadingFeature])

  return (
    <ScreenWrapper
      isLoadingFeature={isLoadingFeature}
      isLoadingScreen={isLoading}
      error={currentTool?.error}
    >
      {currentMapPosition ? (
        <>
          <GoogleMap
            mapType='hybrid'
            isImmediately
            {...currentMapPosition}
            onClick={handlePolygonEditorMapClick}
          >
            {delimitationFeatureCollection ? (
              <GeoJson
                geoJson={delimitationFeatureCollection}
                dataMapStyle={{
                  zIndex: -1,
                  // Allow drawing clicks to reach the map layer.
                  clickable: false,
                  fillOpacity: 0,
                  strokeColor: theme.colors.danger,
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                }}
              />
            ) : null}
            {filteredCoveragesFeatureCollection ? (
              <GeoJson
                geoJson={filteredCoveragesFeatureCollection}
                onClick={handlePolygonClick}
                dataMapStyle={{
                  zIndex: 0,
                  fillColor: theme.colors.tertiary,
                  strokeColor: theme.colors.tertiary,
                }}
              />
            ) : null}
            {selectedItem ? (
              <PolygonEditor
                {...polygonEditor}
                onMapClick={handlePolygonEditorMapClick}
                onMarkerDragEnd={handlePolygonEditorMarkerDragEnd}
                onMarkerClick={handlePolygonEditorMarkerClick}
                onGhostMarkerDragEnd={handlePolygonEditorGhostMarkerDragEnd}
              />
            ) : null}
            {points}
          </GoogleMap>
        </>
      ) : null}
      {!isCompatibleDelimitation ? (
        <OverlayLoading>
          <div className='center'>
            <div className='message'>{incompatibleDelimitationMessage}</div>
          </div>
        </OverlayLoading>
      ) : null}
      <MainMenuControls
        layers={layers}
        baseLayers={baseLayers}
        setBaseLayers={setBaseLayersWithUnsavedConfirmation}
        variables={variables}
        setMapType={() => {}}
        mapType={currentTool?.mapType}
        setMap3DOption={() => {}}
        map3DOption={currentTool?.map3DOption}
        isHighResolutionSpace={true}
        editProps={{
          currentTool,
        }}
      >
            {isCompatibleDelimitation && coveragesFeatureCollection ? (
              <ListInformationWrapper icon={faList}>
                <Aside>
                  <div className='header'>
                    <div className='header-text'>
                      <div className='header-title'>
                        {currentTool?.unitPlural ? currentTool.unitPlural : 'Itens'}
                      </div>
                      <div className='header-subtitle'>
                        {delimitation?.name ? delimitation.name : 'Selecione um município'}
                      </div>
                    </div>
                    <div className='badges'>
                      <div className='badge'>{coveragesFeatureCollection.features.length}</div>
                      {selectedItem ? <div className='badge badge-active'>Editando</div> : null}
                    </div>
                  </div>

                  <div className='items'>
                    <ul>
                      {coveragesFeatureCollection.features.map(item => {
                        const hasCoverageArea = Boolean(item.geometry)
                        const kindLabel =
                          item.properties.kind === 'smartMapping'
                            ? 'Inteligente'
                            : item.properties.kind === 'manualDrawing'
                              ? 'Manual'
                              : item.properties.kind === 'authorityProvided'
                                ? 'Oficial'
                                : item.properties.kind === 'openStreetMapSourced'
                                  ? 'OpenStreetMap'
                                  : item.properties.kind === 'deprecated'
                                    ? 'Descontinuado'
                                    : null
                        const subtitle = kindLabel
                          ? `${hasCoverageArea ? 'Área definida' : 'Sem área'} • ${kindLabel}`
                          : hasCoverageArea
                            ? 'Área definida'
                            : 'Sem área'

                        return (
                          <li
                            key={item.properties.key}
                            id={`li-${item.properties.key}`}
                            className={selectedItem?.key === item.properties.key ? 'is-active' : ''}
                          >
                            <button
                              type='button'
                              className='item-card'
                              onClick={() => {
                                const alreadySelected =
                                  selectedItemRef.current?.key === item.properties.key

                                if (!alreadySelected && hasPendingPolygonEditsRef.current) {
                                  pushUndoSnapshot()
                                  applyPolygon()
                                  setHasPendingPolygonEdits(false)
                                }

                                const selectedItemNext = {
                                  ...item.properties,
                                  coverageArea:
                                    alreadySelected &&
                                    hasPendingPolygonEditsRef.current &&
                                    polygonEditor.polygon
                                      ? polygonEditor.polygon.geometry
                                      : (item.geometry as Polygon | MultiPolygon | null),
                                } as Item

                                if (!alreadySelected) {
                                  setSelectedItem(selectedItemNext)
                                }

                                const navigationPosition = mapNavigationForItem(selectedItemNext)
                                if (navigationPosition) {
                                  setMapNavigationPosition(navigationPosition)
                                }
                              }}
                              aria-current={
                                selectedItem?.key === item.properties.key ? 'true' : undefined
                              }
                            >
                              <span className='item-dot' data-mapped={hasCoverageArea} />
                              <span className='item-text'>
                                <span className='item-title'>{item.properties.name}</span>
                                <span className='item-subtitle'>{subtitle}</span>
                              </span>
                            </button>
                            {selectedItem?.key === item.properties.key && renderItemContent
                              ? renderItemContent(
                                  {
                                    ...item.properties,
                                    coverageArea: item.geometry,
                                  } as Item,
                                  handleChangeItem,
                                )
                              : null}
                          </li>
                        )
                      })}
                    </ul>
                  </div>

                  <div className='footer'>
                    <div className='section actions'>
                      <div className='section-title'>Ações</div>
                      <div className='actions-row'>
                        <ButtonVariants
                          label='Desfazer'
                          disabled={!canUndo || isLoadingFeature}
                          onClick={handleUndo}
                          size='sm'
                          variant='light'
                          title='Ctrl/Cmd+Z'
                        />
                        <ButtonVariants
                          label='Refazer'
                          disabled={!canRedo || isLoadingFeature}
                          onClick={handleRedo}
                          size='sm'
                          variant='light'
                          title='Ctrl/Cmd+Shift+Z'
                        />
                      </div>
                      <ButtonVariants
                        label='Mapeamento inteligente'
                        disabled={save.isLoading || Boolean(smartMappingBlockReason)}
                        onClick={() => setIsSmartMappingOpen(true)}
                        size='sm'
                        variant='light'
                        title={smartMappingBlockReason ?? undefined}
                      />
                      <ButtonVariants
                        onClick={handleSave}
                        disabled={save.isLoading}
                        size='sm'
                        variant='primary'
                        label='Salvar'
                      />
                    </div>

                    <div className='section'>
                      <div className='section-title'>Ferramentas</div>
                      <ButtonVariants
                        label='Remover fragmentos'
                        onClick={handleRemoveFragments}
                        disabled={delimitationHasMultiplePolygons || !coveragesFeatureCollection}
                        size='sm'
                        variant='light'
                      />
                      <ButtonVariants
                        label='Resolver polígonos'
                        onClick={handleResolvePolygons}
                        disabled={!selectedItem || !polygonEditor.polygon || smartMapping.isLoading}
                        size='sm'
                        variant='light'
                      />
                      <ButtonVariants
                        label='Cortar pela delimitação'
                        onClick={handleClipByDelimitation}
                        disabled={!delimitation?.shape || !coveragesFeatureCollection}
                        size='sm'
                        variant='light'
                      />
                      <ButtonVariants
                        label='Preencher toda a área'
                        onClick={handleFillEntireDelimitation}
                        disabled={!selectedItem || !delimitation?.shape || !coveragesFeatureCollection}
                        size='sm'
                        variant='light'
                      />
                      <ButtonVariants
                        label='Apagar polígono selecionado'
                        onClick={handleDeleteSelectedPolygon}
                        disabled={!selectedItem || !hasSelectedPolygon}
                        size='sm'
                        variant='light'
                      />
                      <ButtonVariants
                        label='Limpar todos os polígonos'
                        onClick={handleClearAllPolygons}
                        disabled={!coveragesFeatureCollection || !hasAnyPolygon}
                        size='sm'
                        variant='light'
                      />
                    </div>
                  </div>
                </Aside>
              </ListInformationWrapper>
            ) : null}
          </MainMenuControls>
      {isSmartMappingOpen ? (
        <Modal
          isOpen={isSmartMappingOpen}
          onClose={() => setIsSmartMappingOpen(false)}
          baseWidth='400px'
          styles={{
            overlay: {
              zIndex: 10,
            },
          }}
        >
          <WrapperSmartMapping>
            <label>
              Quantil de outliers em cada classificação <br />
              <input
                type='number'
                value={smartMappingOptions.classificationOutliersQuantile}
                onChange={event =>
                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    classificationOutliersQuantile: Number(event.target.value),
                  })
                }
                required
                min={0}
                step={0.01}
                max={0.3}
              />
            </label>
            <label>
              Quantil de outliers gerais <br />
              <input
                type='number'
                value={smartMappingOptions.generalOutliersQuantile}
                onChange={event =>
                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    generalOutliersQuantile: Number(event.target.value),
                  })
                }
                required
                min={0}
                step={0.01}
                max={0.3}
              />
            </label>

            <label>
              Ocorrências mínimas por classificação <br />
              <input
                type='number'
                value={
                  smartMappingOptions.minOccurrencesPerClassification === 'auto'
                    ? ''
                    : smartMappingOptions.minOccurrencesPerClassification
                }
                onChange={event => {
                  const valueAsNumber = event.currentTarget.valueAsNumber
                  const valueText = event.currentTarget.value.trim()

                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    minOccurrencesPerClassification:
                      valueText === '' || !Number.isFinite(valueAsNumber) ? 'auto' : valueAsNumber,
                  })
                }}
                placeholder='Automático'
                min={1}
                step={1}
              />
            </label>

            <label>
              <input
                type='checkbox'
                checked={smartMappingOptions.useCustomDelimitationPolygon}
                onChange={event =>
                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    useCustomDelimitationPolygon: event.target.checked,
                  })
                }
              />{' '}
              Cortar pela delimitação geográfica
            </label>
            {(currentTool?.key === 'healthUnits' ||
              currentTool?.key === 'healthTeams' ||
              currentTool?.key === 'healthProfessionals') &&
            smartMappingOptions.useCustomDelimitationPolygon ? (
              <label>
                <input
                  type='checkbox'
                  checked={smartMappingOptions.usePointsDelimitationPolygon ?? true}
                  onChange={event =>
                    setSmartMappingOptions({
                      ...smartMappingOptions,
                      usePointsDelimitationPolygon: event.target.checked,
                    })
                  }
                />{' '}
                Limitar pelo contorno dos pontos
              </label>
            ) : null}
            {currentTool?.key === 'healthUnits' ||
            currentTool?.key === 'healthTeams' ||
            currentTool?.key === 'healthProfessionals' ? (
              <label>
                <input
                  type='checkbox'
                  checked={Boolean(smartMappingOptions.respectCentralPoint)}
                  onChange={event =>
                    setSmartMappingOptions({
                      ...smartMappingOptions,
                      respectCentralPoint: event.target.checked,
                    })
                  }
                />{' '}
                Respeitar o ponto central
              </label>
            ) : null}
            <label>
              <input
                type='checkbox'
                checked={smartMappingOptions.withTopology}
                onChange={event =>
                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    withTopology: event.target.checked,
                  })
                }
                disabled
              />{' '}
              Topologia
            </label>
            <ButtonVariants
              label='Atualizar polígonos'
              onClick={handleUpdatePolygonsUsingSmartMapping}
              disabled={smartMapping.isLoading}
              size='sm'
              variant='primary'
            />
          </WrapperSmartMapping>
        </Modal>
      ) : null}
    </ScreenWrapper>
  )
}
